import { WIDGET_COMPONENT_MAP } from './charts/widgets.tsx';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { Box, IconButton, Tooltip, CircularProgress, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';

type WidgetCardProps = {
  widget:        WidgetInstance;
  osmName:       string;
  dateFromValue: Dayjs | null;
  dateToValue:   Dayjs | null;
  editMode:      boolean;
  index:         number;
  total:         number;
  onEdit:        (w: WidgetInstance) => void;
  onDelete:      (id: number) => void;
  onMove:        (index: number, direction: 'up' | 'down') => void;
};

export function WidgetCard({
  widget, osmName, dateFromValue, dateToValue,
  editMode, index, total, onEdit, onDelete, onMove,
}: WidgetCardProps) {
  const WidgetComponent = WIDGET_COMPONENT_MAP[widget.widget_type_id];

  return (
    <Box
      sx={{
        position:    'relative',
        border:      editMode ? '2px dashed' : '1px solid',
        borderColor: editMode ? 'primary.light' : 'divider',
        borderRadius: 2,
        p: 1,
        height:    'fit-content',
        minHeight: 350,
        width:     '100%',
        boxSizing: 'border-box',
      }}
    >
      {widget.title && (
        <Typography variant="subtitle2" align="center" gutterBottom>
          {widget.title}
        </Typography>
      )}

      {WidgetComponent ? (
        <WidgetComponent
          osmName={osmName}
          dateFromValue={dateFromValue}
          dateToValue={dateToValue}
          chartType={widget.chart_type as ChartType}
          title={widget.title}
          config={widget.config}
          colour={widget.colour ?? undefined}
          min={widget.min_value ?? undefined}
          max={widget.max_value ?? undefined}
          measurement={widget.measurement ?? undefined}
        />
      ) : (
        <Typography color="error">Unknown widget type: {widget.widget_type_id}</Typography>
      )}

      {editMode && (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mt: 1 }}>
          <Tooltip title="Move left">
            <span>
              <IconButton size="small" onClick={() => onMove(index, 'up')} disabled={index === 0}>
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Move right">
            <span>
              <IconButton size="small" onClick={() => onMove(index, 'down')} disabled={index === total - 1}>
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => onEdit(widget)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => onDelete(widget.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
}
